import cs from 'classnames';
import React, {
  useState, ChangeEventHandler, FormEventHandler, useCallback, useEffect, useRef,
} from 'react';
import {
  Alert, Button, Col, Form, Modal, Row, Spinner, ProgressBar,
} from 'react-bootstrap';
import { SubtitleInput } from './SubtitleInput';
import { v4 as uuidV4 } from 'uuid';
import { useApiService } from '../../../../../contexts/ApiServiceContext/ApiServiceContext';
import { useAsyncOperationState } from '../../../../../dorian-shared/hooks/useAsyncOperationState';
import { type VideoConfig } from '../../../../../dorian-shared/types/media/MediaConfigs';
import { convertVideo, downloadBlob } from '../../../../../services/ffmpeg';
import { api } from '../../../../api';
import { VideoPreview } from '../VideoPreview/VideoPreview';
import { useRefetchVideo } from '../VideosProvider/VideosProvider';
import classNames from './UploadVideo.module.scss';

const acceptFileTypes = '.mp4';
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const UPLOAD_PHASE_LABEL_ID = 'upload-video-phase-label';

const requestConfig = {
  headers: {
    'content-type': 'multipart/form-data',
  },
};

export function UploadVideo({
  onUpload,
  onCancel,
}: {onUpload: VoidFunction, onCancel: VoidFunction}) {
  const abortController = useRef<AbortController | null>(null);
  // Late callbacks must not touch a component that is gone, and a superseded
  // upload must not drive the bar for the one that replaced it.
  const isMountedRef = useRef(true);
  const uploadGenerationRef = useRef(0);
  const apiService = useApiService();
  const refetchVideo = useRefetchVideo();
  const [loading, setLoading] = useState(false);
  const [validatedForm, setValidatedForm] = useState(false);

  // video
  const [isVideoConverting, setIsVideoConverting] = useState(false);
  const [videoConversionProgress, setVideoConversionProgress] = useState(0);
  const [convertedVideo, setConvertedVideo] = useState<Blob | null>(null);
  const [convertedVideoURL, setConvertedVideoURL] = useState<string | null>(null);

  // upload
  // null = the request reported no total, so the bar has to run indeterminate.
  const [uploadProgress, setUploadProgress] = useState<number | null>(0);

  const [videoTitle, setVideoTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [videoConfig, setVideoConfig] = useState<VideoConfig | null>(null);
  const [subtitlesFile, setSubtitlesFile] = useState<File | null>(null);


  const [,
    {
      isLoading,
      setToLoading,
      setToSuccess,
      setToError,
    },
  ] = useAsyncOperationState();
  const loadConfig = useCallback(async () => {
    setToLoading();
    try {
      const _videoConfig = (await apiService.fetchVideoConfig()) ?? {};
      setVideoConfig(_videoConfig);
      setToSuccess();
    } catch (e) {
      setToError();
    }
  }, [apiService, setToError, setToLoading, setToSuccess]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const tryPreprocessFile = async (file: File) => {
    if (!videoConfig) {
      return;
    }
    setIsVideoConverting(true);
    setVideoConversionProgress(0);
    abortController.current = new AbortController();
    try {
      const _convertedVideo = await convertVideo(file, videoConfig, (progress) => {
        if (!isMountedRef.current) {
          return;
        }
        setVideoConversionProgress(Math.floor(progress * 100));
      }, abortController.current.signal);
      abortController.current = null;
      if (!isMountedRef.current) {
        return;
      }
      setConvertedVideo(_convertedVideo);
      setConvertedVideoURL(URL.createObjectURL(_convertedVideo));
    } catch (e) {
      if (!isMountedRef.current) {
        return;
      }
      setErrorMessage((e as Error).message);
    }
    setIsVideoConverting(false);
  };

  useEffect(() => {
    // Re-arm on every setup, not just on the first: StrictMode replays
    // setup/cleanup, and the useRef initializer does not run again, so a
    // cleanup-then-setup pair would otherwise leave this false while mounted.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Bump the generation as well, so an in-flight upload's handlers see a
      // mismatch even if they somehow read the ref before the flag.
      uploadGenerationRef.current += 1;
      abortController.current?.abort();
    };
  }, []);

  const onDownloadConvertedVideo = () => {
    if (!convertedVideo) {
      return;
    }

    downloadBlob(convertedVideo, 'converted.mp4');
  };

  const handleFormSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    if (!convertedVideo) {
      return;
    }

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      setValidatedForm(true);
      return;
    }

    const formData = new FormData();
    const alias = uuidV4();
    formData.append('video', convertedVideo, `${alias}.mp4`);
    formData.append('alias', alias);
    formData.append('title', videoTitle);

    // include subtitles if provided
    if (subtitlesFile) {
      formData.append('subtitles', subtitlesFile, `${alias}.vtt`);
    }

    uploadGenerationRef.current += 1;
    const generation = uploadGenerationRef.current;
    // Only the newest request of a still-mounted modal may touch state.
    const isCurrentRequest = () => isMountedRef.current
      && uploadGenerationRef.current === generation;

    setLoading(true);
    setUploadProgress(0);

    api.post('/v1/video', formData, {
      ...requestConfig,
      onUploadProgress: (progressEvent: ProgressEvent) => {
        if (!isCurrentRequest()) {
          return;
        }
        const { loaded, total } = progressEvent;
        if (!total) {
          setUploadProgress(null);
          return;
        }
        // Floor, not round: 99.6% must not read as 100%. Hold at 99 until the
        // last byte is actually out, so 100 only ever means "handed off".
        const percent = Math.floor((loaded * 100) / total);
        setUploadProgress(loaded >= total ? 100 : Math.min(percent, 99));
      },
    })
      .then(() => {
        if (!isCurrentRequest()) {
          return;
        }
        setLoading(false);
        refetchVideo();
        onUpload();
      })
      .catch(() => {
        if (!isCurrentRequest()) {
          return;
        }
        setLoading(false);
        setUploadProgress(0);
      });
  };

  const hideErrorMessage = () => {
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  // TODO: Ask for format and min/max size file
  const handleSelectFile: ChangeEventHandler<HTMLInputElement> = (event) => {
    hideErrorMessage();
    const file = event?.target?.files?.[0];

    if (!file) {
      setErrorMessage('File not selected.');
      return;
    }

    if (file.type.indexOf('mp4') === -1) {
      setErrorMessage('Available only *.mp4 format');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage('Maximum file size is 100MB');
      return;
    }

    if (!videoTitle) {
      setVideoTitle(file.name.replace(/\.[^\\/.]+$/, ''));
    }

    tryPreprocessFile(file);
  };

  // Upload bar. With no total to measure against the bar runs indeterminate:
  // full width, animated stripes, no percentage to mislead with, and no
  // aria-valuenow — a filled bar reporting 100 would claim the upload is done.
  const isUploadIndeterminate = uploadProgress === null;
  const uploadBarNow = uploadProgress ?? 0;
  const isUploadSent = !isUploadIndeterminate && uploadBarNow >= 100;
  let uploadBarLabel = 'Uploading...';
  if (isUploadSent) {
    uploadBarLabel = 'Finalizing...';
  } else if (!isUploadIndeterminate) {
    uploadBarLabel = `Uploading... ${uploadBarNow}%`;
  }

  return (
    <Modal
      show
      size="lg"
    >
      <Form
        noValidate
        validated={validatedForm}
        className={classNames.form}
        onSubmit={handleFormSubmit}
      >
        <Modal.Header>
          <Modal.Title
            className={classNames.modalTitle}
          >
            Upload video
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className={classNames.modalBody}
        >
          <Row>
            <Form.Group
              as={Col}
              sm="12"
              controlId="video-file"
              className={classNames.formGroup}
            >
              <Form.Label className={classNames.formLabel}>
                Video File
              </Form.Label>
              <Form.Control
                required
                name="video-file"
                type="file"
                placeholder="Image"
                accept={acceptFileTypes}
                onChange={handleSelectFile}
              />
              <Form.Control.Feedback type="invalid">
                Please choose a video.
              </Form.Control.Feedback>
              {isVideoConverting && (
                <>
                  {/* Outside the bar, so the phase stays readable at 0%, where
                      there is no filled area to print a label into. */}
                  <div className={classNames.progressLabel}>
                    {`Processing video... ${videoConversionProgress}%`}
                  </div>
                  <ProgressBar>
                    <ProgressBar
                      striped
                      variant="success"
                      now={videoConversionProgress}
                    />
                  </ProgressBar>
                </>
              )}
              {loading && (
                <>
                  {/* The phase is the only thing the indeterminate bar can
                      report, so announce it: polite, and referenced by the
                      bar itself. */}
                  <div
                    id={UPLOAD_PHASE_LABEL_ID}
                    aria-live="polite"
                    className={classNames.progressLabel}
                  >
                    {uploadBarLabel}
                  </div>
                  {isUploadIndeterminate ? (
                    // Hand-rolled rather than react-bootstrap's ProgressBar,
                    // which requires a numeric `now` and would publish
                    // aria-valuenow="100" for an upload that is still running.
                    <div
                      role="progressbar"
                      aria-busy="true"
                      aria-label="Uploading"
                      aria-describedby={UPLOAD_PHASE_LABEL_ID}
                      className="progress"
                    >
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                        style={{ width: '100%' }}
                      />
                    </div>
                  ) : (
                    <ProgressBar>
                      {/* aria-describedby lands on the child, which is the
                          element that actually carries role="progressbar";
                          the outer ProgressBar renders the plain wrapper. */}
                      <ProgressBar
                        striped
                        animated={isUploadSent}
                        variant="success"
                        now={uploadBarNow}
                        aria-describedby={UPLOAD_PHASE_LABEL_ID}
                      />
                    </ProgressBar>
                  )}
                </>
              )}
              {convertedVideoURL && (
                <VideoPreview
                  videoUrl={convertedVideoURL}
                  subtitleUrl={subtitlesFile ? URL.createObjectURL(subtitlesFile) : undefined}
                />
              )}
            </Form.Group>

            <Form.Group as={Col} sm="8" controlId="video-title">
              <Form.Label className={classNames.formLabel}>
                Title
              </Form.Label>
              <Form.Control
                required
                value={videoTitle}
                name="video-title"
                type="text"
                size="sm"
                placeholder="Video title"
                pattern="^([A-Za-z]|[0-9]|_|-|)+$"
                onChange={(event) => {
                  hideErrorMessage();
                  setVideoTitle(event.target.value);
                }}
              />
              <Form.Control.Feedback type="invalid">
                Please use characters A-Z,a-z,0-9,_,-
              </Form.Control.Feedback>
            </Form.Group>
            {/* new SubtitleInput component */}
            <Form.Group as={Col} sm="8" controlId="video-subtitle-file">
            <SubtitleInput
              onValidFile={(file) => { hideErrorMessage(); setSubtitlesFile(file); }}
              onError={setErrorMessage}
            />
            </Form.Group>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <div>
            <Button
              variant="success"
              disabled={!convertedVideo}
              className={classNames.button}
              onClick={onDownloadConvertedVideo}
            >
              Download
            </Button>

            <Button
              type="reset"
              variant="secondary"
              className={classNames.button}
              onClick={onCancel}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="primary"
              disabled={isVideoConverting}
              className={classNames.button}
            >
              {loading && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
              Upload
            </Button>
          </div>
          {
            errorMessage && (
              <Col md={12}>
                <Alert variant="danger">
                  {errorMessage}
                </Alert>
              </Col>
            )
          }
        </Modal.Footer>
      </Form>
      {isLoading
          && (
            <>
              <div className={cs({ overlay: isLoading })} />
              <Spinner
                variant="primary"
                animation="border"
                className="spinner"
              />
            </>
          )}
    </Modal>
  );
}
